<script lang="ts">
  // Libs
  import { createEventDispatcher } from "svelte";

  // Services
  import Flash from "@shared/services/flashv2.js";
  import { saveOrderForLater } from "@retailer/svelte/Cart/SavedForLater/Api.svelte";
  import { restrictRepAccess } from "@retailer/services/preview_forbidden_flash";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import {
    getStockSiteFulfillmentData,
    getFulfillmentInformation,
  } from "@retailer/services/order_fulfillment_service";

  // Stores
  import layoutStore from "@shared/svelte/Stores/LayoutStore";
  import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";

  // Components
  import OrderBackupNotesModal from "@retailer/svelte/Cart/Page/OrderBackupNotesModal.svelte";
  import OrderFulfillmentModal from "@retailer/svelte/Cart/Page/OrderFulfillment/OrderFulfillmentModal.svelte";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";

  //Types
  import type {
    CartOrderType,
    FulfillmentInformationType,
    CartStockSiteType,
  } from "@shared/types/Cart";
  import NotesAndBackup from "@retailer/svelte/Shared/NotesAndBackup.svelte";

  import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  // Props
  export let cartService: any;
  export let order: CartOrderType;
  export let showLoading: boolean;

  // Data
  let showNoteModal = false;
  let dispatch = createEventDispatcher();
  let savingForLater = false;
  let showOrderFulfillmentModal = false;
  let fulfillmentInformationByDistributor: FulfillmentInformationType[];
  let spinSubmitButton = false;
  let submittingOrder: boolean = false;
  let stockSiteData;

  // Functions
  const saveNote = async () => {
    let backupNotes = order.backup_notes;
    let orderNotes = order.retailer_notes;

    await cartService.saveNote(order.id, { orderNotes, backupNotes }).catch(err => {
      if (err.status === 404) {
        Flash.now({
          type: "error",
          header: "Order was not found",
          message: "All items were removed from your order",
        });
      }
    });

    cartService.load();
    showNoteModal = false;
  };

  const OHLQOrderPaymentMethodsEnabled = stagedRolloutCheck(
    "marketplace__ohlq_order_payment_methods",
  );

  const submitOrder = async () => {
    if (orderingIsDisabled) return;
    if (submittingOrder || showLoading) return;
    if (!order) return;
    if (restrictRepAccess()) return;

    if (await orderCannotSubmit()) {
      dispatch("submitWithErrors");
      return;
    } else if (isAFulfillmentDateOrder()) {
      spinSubmitButton = true;
      if (pickupStockSites || deliveryStockSites) {
        fulfillmentInformationByDistributor = getFulfillmentInformation(
          order,
          deliveryStockSites,
          pickupStockSites,
        );
        spinSubmitButton = false;
        showOrderFulfillmentModal = true;
      } else {
        cartService.getFulfillmentInformation((res: { data: FulfillmentInformationType[] }) => {
          fulfillmentInformationByDistributor = res.data;
          spinSubmitButton = false;
          showOrderFulfillmentModal = true;
        });
      }
    } else {
      submittingOrder = true;
      showLoading = true;

      try {
        await cartService.submitIndividualOrder(order.id);
        submittingOrder = false;
        showLoading = false;
      } catch (error) {
        submittingOrder = false;
        showLoading = false;
        throw error;
      }
    }
  };

  const saveAllForLaterWrapper = function () {
    if (savingForLater) return;
    savingForLater = true;
    saveOrderForLater(cartService, order);
    savingForLater = false;
  };

  const openNoteModal = () => (showNoteModal = true);

  const closeOrderFulfillmentModal = () => {
    showOrderFulfillmentModal = false;
  };

  $: orderCannotSubmit = async () => {
    await cartService.load();
    return order.products.length === 0 || !cartService.canSubmitIndividualOrder(order);
  };
  $: orderingIsDisabled = order?.distributor?.ordering_disabled;
  $: hasNotes = order.retailer_notes || order.backup_notes;
  $: noteVerb = hasNotes ? "Edit" : "Add";
  $: orderWarehouses = order.distributor?.uses_warehouse_pickup ? order?.order_warehouses : [];
  $: isAFulfillmentDateOrder = () => {
    return order.distributor?.id !== VT_802_SPIRITS_DISTRIBUTOR_ID && orderWarehouses.length > 0;
  };
  $: {
    if (
      OHLQOrderPaymentMethodsEnabled &&
      order.distributor?.uses_wine_cellar &&
      order.distributor?.uses_warehouse_pickup &&
      isAFulfillmentDateOrder()
    ) {
      stockSiteData = getStockSiteFulfillmentData(order);
    }
  }
  $: pickupStockSites =
    !$stockSiteData || $stockSiteData?.loading || $stockSiteData?.error
      ? null
      : (($stockSiteData.data?.pickupStockSites?.nodes || []) as CartStockSiteType[]);
  $: deliveryStockSites =
    !$stockSiteData || $stockSiteData?.loading || $stockSiteData?.error
      ? null
      : (($stockSiteData.data?.deliveryStockSites?.nodes || []) as CartStockSiteType[]);
</script>

<div
  class="fizz-inset-square-16 fizz-align-content-start fizz-surface"
  class:fizz-flex-row-12-responsive={$layoutStore.isMobile}
  class:fizz-flex-row-32={$layoutStore.isDesktop}
>
  <div class="fizz-flex-item fizz-full-width">
    {#if hasNotes}
      <div class="fizz-stack-4">
        <NotesAndBackup {order} background="fizz-surface-yellow" padding={8} />
      </div>
    {/if}
    <button
      class="fizz-button-text fizz-button-small"
      disabled={showLoading}
      on:click={openNoteModal}
    >
      {noteVerb} notes for sales reps
    </button>
  </div>
  <div class="fizz-flex-row-8 fizz-space-between" class:fizz-full-width={$layoutStore.isMobile}>
    <button
      class="fizz-button fizz-button-small fizz-flex-item"
      id="save-order-{order.distributor.id}"
      disabled={showLoading}
      on:click={saveAllForLaterWrapper}
    >
      Save for later
    </button>
    {#if !$CurrentRetailerStore?.has_punchout}
      <Authorization authName={"carts.submit"}>
        <button
          class="fizz-button fizz-button-small fizz-flex-item"
          on:click={submitOrder}
          class:fizz-button-spinner={spinSubmitButton}
          disabled={orderingIsDisabled || showLoading || submittingOrder || savingForLater}
        >
          {#if submittingOrder}
            Submitting...
          {:else if isAFulfillmentDateOrder() && $layoutStore.isDesktop}
            Select date and send order
          {:else}
            Send order
          {/if}
        </button>
      </Authorization>
    {/if}
  </div>
</div>

{#if showNoteModal}
  <OrderBackupNotesModal bind:show={showNoteModal} {order} {saveNote} />
{/if}

{#if showOrderFulfillmentModal}
  <OrderFulfillmentModal
    closeModal={closeOrderFulfillmentModal}
    {fulfillmentInformationByDistributor}
    cartSubmit={() => cartService.submitIndividualOrder(order.id)}
    fulfillmentUpdate={cartService.batchUpdateOrderWarehouses}
    batchUpdateOrders={cartService.batchUpdateOrders}
    {OHLQOrderPaymentMethodsEnabled}
    distributorUsesWineCellar={OHLQOrderPaymentMethodsEnabled &&
      order.distributor?.uses_wine_cellar}
  />
{/if}
